import { useEffect, useMemo, useRef, useState } from "react";
import * as ContextMenu from "@radix-ui/react-context-menu";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronRight,
  ImagePlus,
  LayoutGrid,
  Link2,
  MoreHorizontal,
  Palette,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  StickyNote,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { SHARED_FOLDER_COLORS, type WorkspaceItem } from "@hydra-notes/shared";
import type * as Y from "yjs";
import { getData, useDataContextId, useWorkspaceItems } from "@/data/store";
import { useDocPresence } from "@/data/presence";
import { PresenceChips } from "@/ui/PresenceChips";
import { docName } from "@hydra-notes/shared";
import type { NoteHandle } from "@/data/notes";
import { uploadAttachment } from "@/editor/attachments";
import { useAuth } from "@/data/auth";
import { useSignInDialog } from "@/components/SignInDialog";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import { deleteWorkspaceItemForever } from "@/data/permanentDelete";
import { useUi, type CardMode } from "@/store/ui";
import {
  CARD_DRAG_TYPE,
  archiveCard,
  draggedCardId,
  moveCardToBoard,
  restoreArchivedCard,
  restoreTrashedCard,
  trashCard,
} from "./dnd";
import s from "./CardsView.module.css";

const IMAGE_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;
const URL_RE = /https?:\/\/[^\s<>)\]]+/g;

interface CardImage {
  alt: string;
  url: string;
}

function cardImages(body: string): CardImage[] {
  return [...body.matchAll(IMAGE_RE)].map((m) => ({ alt: m[1] || "Card image", url: m[2]! }));
}

function cardText(body: string): string {
  return body.replace(IMAGE_RE, "").trim();
}

function cardLinks(body: string): string[] {
  const withoutImages = body.replace(IMAGE_RE, "");
  return [...new Set(withoutImages.match(URL_RE) ?? [])].slice(0, 3);
}

/** One-splice text replacement preserves concurrent edits better than reset. */
function replaceText(text: Y.Text, next: string): void {
  const current = text.toString();
  if (current === next) return;
  let prefix = 0;
  while (prefix < current.length && prefix < next.length && current[prefix] === next[prefix]) prefix++;
  let suffix = 0;
  while (
    suffix < current.length - prefix &&
    suffix < next.length - prefix &&
    current[current.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix++;
  }
  text.doc?.transact(() => {
    const remove = current.length - prefix - suffix;
    if (remove > 0) text.delete(prefix, remove);
    const insert = next.slice(prefix, next.length - suffix);
    if (insert) text.insert(prefix, insert);
  }, "cards-editor");
}

export function CardsView() {
  const items = useWorkspaceItems();
  const cards = useMemo(() => items.filter((i) => i.type === "card"), [items]);
  const boards = useMemo(
    () => items.filter((i) => i.type === "cardboard" && !i.archivedAt && !i.trashedAt),
    [items],
  );
  const mode = useUi((state) => state.cardMode);
  const activeBoard = useUi((state) => state.activeCardBoard);
  const spaceId = useDataContextId();
  const present = useDocPresence(spaceId, getData().sync ? docName.workspace(spaceId) : undefined);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bodies, setBodies] = useState<Record<string, string>>({});
  const handles = useRef(new Map<string, NoteHandle>());
  const observers = useRef(new Map<string, () => void>());

  // Keep every card body live while this product is open. NoteDocManager
  // refcounts these handles, so the editor and this grid share one Y.Doc.
  useEffect(() => {
    const data = getData();
    const wanted = new Set(cards.map((c) => c.id));
    for (const [id, h] of handles.current) {
      if (!wanted.has(id)) {
        const observer = observers.current.get(id);
        if (observer) h.text.unobserve(observer);
        observers.current.delete(id);
        h.release();
        handles.current.delete(id);
      }
    }
    for (const card of cards) {
      if (handles.current.has(card.id)) continue;
      const h = data.notes.open(card.id);
      handles.current.set(card.id, h);
      void h.whenLoaded.then(() => {
        // The card or this whole view may have disappeared while IndexedDB
        // was loading. Only the handle still owned by this instance may
        // attach an observer.
        if (handles.current.get(card.id) !== h) return;
        const sync = () => setBodies((old) => ({ ...old, [card.id]: h.text.toString() }));
        observers.current.set(card.id, sync);
        h.text.observe(sync);
        sync();
      });
    }
  }, [cards]);

  useEffect(
    () => () => {
      for (const [id, h] of handles.current) {
        const observer = observers.current.get(id);
        if (observer) h.text.unobserve(observer);
        h.release();
      }
      handles.current.clear();
      observers.current.clear();
    },
    [],
  );

  const setBody = (id: string, body: string) => {
    const h = handles.current.get(id);
    if (!h) return;
    replaceText(h.text, body);
    getData().workspace.touch(id);
  };

  useEffect(() => {
    if (activeBoard !== "all" && !boards.some((board) => board.id === activeBoard)) {
      useUi.getState().openCardBoard("all");
    }
  }, [activeBoard, boards]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((card) => {
      const inMode =
        mode === "trash"
          ? !!card.trashedAt
          : mode === "archive"
            ? !!card.archivedAt && !card.trashedAt
            : !card.archivedAt && !card.trashedAt;
      if (!inMode) return false;
      if (mode === "cards" && activeBoard !== "all" && card.parentId !== activeBoard) return false;
      return !q || card.title.toLowerCase().includes(q) || (bodies[card.id] ?? "").toLowerCase().includes(q);
    });
  }, [cards, mode, activeBoard, query, bodies]);

  const pinned = mode === "cards" ? visible.filter((c) => c.pinned) : [];
  const others = mode === "cards" ? visible.filter((c) => !c.pinned) : visible;
  const editing = cards.find((c) => c.id === editingId) ?? null;
  const currentBoard = boards.find((board) => board.id === activeBoard);
  const viewTitle = mode === "archive" ? "Archive" : mode === "trash" ? "Trash" : currentBoard?.title || "All cards";

  return (
    <div className={s.page} data-cards-view>
      <header className={s.header}>
        <div className={s.brand}><StickyNote size={21} /><span>{viewTitle}</span></div>
        <PresenceChips present={present} verb="is here" writingVerb="is editing" />
        <label className={s.search}>
          <Search size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search cards" />
          {query ? <button type="button" onClick={() => setQuery("")}><X size={14} /></button> : null}
        </label>
      </header>

      <main className={s.content}>
        {mode === "cards" ? <CardComposer boardId={activeBoard === "all" ? null : activeBoard} /> : null}
        {pinned.length ? <CardSection label="Pinned" cards={pinned} boards={boards} bodies={bodies} mode={mode} onOpen={setEditingId} /> : null}
        {others.length ? (
          <CardSection
            label={pinned.length ? "Others" : mode === "archive" ? "Archived" : mode === "trash" ? "Trash" : undefined}
            cards={others}
            boards={boards}
            bodies={bodies}
            mode={mode}
            onOpen={setEditingId}
          />
        ) : null}
        {visible.length === 0 ? <EmptyState mode={mode} filtered={!!query} /> : null}
      </main>

      <CardEditor
        card={editing}
        body={editing ? (bodies[editing.id] ?? "") : ""}
        onBody={setBody}
        onClose={() => setEditingId(null)}
      />
    </div>
  );
}

function CardComposer({ boardId }: { boardId: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [color, setColor] = useState("gray");
  const [pinned, setPinned] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const saving = useRef(false);
  const titleRef = useRef<HTMLInputElement>(null);

  const clearDraft = (collapse: boolean) => {
    setTitle("");
    setBody("");
    setColor("gray");
    setPinned(false);
    setFiles([]);
    if (collapse) setExpanded(false);
    else requestAnimationFrame(() => titleRef.current?.focus());
  };

  const choosePhotos = (next: File[]) => {
    if (useAuth.getState().status !== "signedIn") {
      toast.info("Sign in to add photos. Photos count toward your storage quota.", {
        label: "Sign in",
        onClick: () => useSignInDialog.getState().setOpen(true),
      });
      return;
    }
    setFiles((old) => [...old, ...next]);
  };

  const save = async () => {
    if (saving.current || (!title.trim() && !body.trim())) return;
    saving.current = true;
    const data = getData();
    const id = data.workspace.createItem("card", boardId, title.trim());
    if (color !== "gray") data.workspace.setColorKey(id, color);
    if (pinned) data.workspace.setPinned(id, true);
    const h = data.notes.open(id);
    await h.whenLoaded;
    if (body) h.text.insert(0, body);
    for (const file of files) {
      try {
        const uploaded = await uploadAttachment(file, { noteId: id });
        const line = `![${uploaded.name}](${uploaded.url})`;
        h.text.insert(h.text.length, `${h.text.length ? "\n" : ""}${line}`);
      } catch (err) {
        toast.error(`Photo upload failed: ${err instanceof Error ? err.message : "unknown error"}`);
      }
    }
    h.release();
    data.workspace.touch(id);
    saving.current = false;
    clearDraft(false);
  };

  const submitOnEnter = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void save();
  };

  if (!expanded) {
    return <button type="button" data-card-composer className={s.takeCard} onClick={() => setExpanded(true)}><Plus size={17} />Take a card...</button>;
  }

  return (
    <div className={s.composer} style={{ background: `var(--card-${color})` }}>
      <div className={s.composerTop}>
        <input ref={titleRef} autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={submitOnEnter} placeholder="Subject" />
        <IconButton label={pinned ? "Unpin" : "Pin"} active={pinned} onClick={() => setPinned(!pinned)}>{pinned ? <PinOff size={16} /> : <Pin size={16} />}</IconButton>
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={submitOnEnter} placeholder="Description" rows={3} />
      {files.length ? <div className={s.pendingPhotos}>{files.map((f) => <span key={`${f.name}-${f.size}`}>{f.name}</span>)}</div> : null}
      <div className={s.composerActions}>
        <PhotoPicker onFiles={choosePhotos} />
        <ColorPicker value={color} onChange={setColor} />
        <span className={s.spacer} />
        <span className={s.enterHint}>Enter to add · Shift+Enter for a new line</span>
        <button type="button" className={s.textButton} onClick={() => clearDraft(true)}>Cancel</button>
        <button type="button" className={s.addButton} disabled={!title.trim() && !body.trim()} onClick={() => void save()}>Add</button>
      </div>
    </div>
  );
}

function CardSection({ label, cards, boards, bodies, mode, onOpen }: { label?: string; cards: WorkspaceItem[]; boards: WorkspaceItem[]; bodies: Record<string, string>; mode: CardMode; onOpen: (id: string) => void }) {
  return <section className={s.section}>{label ? <h2>{label}</h2> : null}<div className={s.masonry}>{cards.map((card) => <CardTile key={card.id} card={card} boards={boards} body={bodies[card.id] ?? ""} mode={mode} onOpen={() => onOpen(card.id)} />)}</div></section>;
}

function CardTile({ card, boards, body, mode, onOpen }: { card: WorkspaceItem; boards: WorkspaceItem[]; body: string; mode: CardMode; onOpen: () => void }) {
  const images = cardImages(body);
  const text = cardText(body);
  const links = cardLinks(body);
  const data = getData();
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>
        <article
          className={s.card}
          style={{ background: `var(--card-${card.colorKey ?? "gray"})` }}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData(CARD_DRAG_TYPE, card.id);
          }}
          onDragOver={(e) => {
            if (mode === "cards" && e.dataTransfer.types.includes(CARD_DRAG_TYPE)) e.preventDefault();
          }}
          onDrop={(e) => {
            if (mode !== "cards") return;
            e.preventDefault();
            const source = draggedCardId(e.dataTransfer);
            if (source && source !== card.id) moveCardToBoard(source, card.parentId, card.id);
          }}
        >
          <button type="button" className={s.cardOpen} onClick={onOpen} aria-label={`Open ${card.title || "untitled card"}`}>
            {images.length ? <div className={[s.imageGrid, images.length > 1 ? s.imageGridMany : ""].join(" ")}>{images.slice(0, 4).map((img, i) => <img key={`${img.url}-${i}`} src={img.url} alt={img.alt} loading="lazy" />)}</div> : null}
            <div className={s.cardCopy}>
              {card.title ? <h3>{card.title}</h3> : null}
              {text ? <p>{text}</p> : null}
              {links[0] ? <LinkPreview url={links[0]} compact /> : null}
            </div>
          </button>
          {!card.trashedAt ? <div className={s.cardActions}>
            <IconButton label={card.pinned ? "Unpin" : "Pin"} active={!!card.pinned} onClick={() => data.workspace.setPinned(card.id, !card.pinned)}>{card.pinned ? <PinOff size={14} /> : <Pin size={14} />}</IconButton>
            <ColorPicker value={card.colorKey ?? "gray"} onChange={(value) => data.workspace.setColorKey(card.id, value === "gray" ? undefined : value)} />
            <IconButton label={card.archivedAt ? "Restore from archive" : "Archive"} onClick={() => card.archivedAt ? restoreArchivedCard(card.id) : archiveCard(card.id)}>{card.archivedAt ? <ArchiveRestore size={14} /> : <Archive size={14} />}</IconButton>
            <IconButton label="Move to trash" onClick={() => trashCard(card.id)}><Trash2 size={14} /></IconButton>
            <CardActionsMenu card={card} boards={boards} onOpen={onOpen} />
          </div> : <div className={s.cardActions}>
            <IconButton label="Restore" onClick={() => restoreTrashedCard(card.id)}><Undo2 size={14} /></IconButton>
            <IconButton label="Delete forever" onClick={() => void deleteForever(card)}><Trash2 size={14} /></IconButton>
            <CardActionsMenu card={card} boards={boards} onOpen={onOpen} />
          </div>}
        </article>
      </ContextMenu.Trigger>
      <CardContextMenu card={card} boards={boards} onOpen={onOpen} />
    </ContextMenu.Root>
  );
}

function CardContextMenu({ card, boards, onOpen }: { card: WorkspaceItem; boards: WorkspaceItem[]; onOpen: () => void }) {
  const data = getData();
  const inTrash = !!card.trashedAt;
  return (
    <ContextMenu.Portal>
      <ContextMenu.Content className="hn-menu">
        <ContextMenu.Item className="hn-menu-item" onSelect={onOpen}>
          <Pencil /> Edit card
        </ContextMenu.Item>
        {!inTrash ? (
          <ContextMenu.Item className="hn-menu-item" onSelect={() => data.workspace.setPinned(card.id, !card.pinned)}>
            {card.pinned ? <PinOff /> : <Pin />} {card.pinned ? "Unpin" : "Pin"}
          </ContextMenu.Item>
        ) : null}
        <ContextMenu.Sub>
          <ContextMenu.SubTrigger className="hn-menu-item">
            <LayoutGrid /> Move to board <span className="hn-menu-hint"><ChevronRight /></span>
          </ContextMenu.SubTrigger>
          <ContextMenu.Portal>
            <ContextMenu.SubContent className="hn-menu" sideOffset={4} alignOffset={-4}>
              <ContextMenu.Item className="hn-menu-item" onSelect={() => moveCardToBoard(card.id, null)}>
                {card.parentId === null ? <Check /> : <LayoutGrid />} All cards
              </ContextMenu.Item>
              {boards.map((board) => (
                <ContextMenu.Item key={board.id} className="hn-menu-item" onSelect={() => moveCardToBoard(card.id, board.id)}>
                  {card.parentId === board.id ? <Check /> : <StickyNote />} {board.title || "Untitled"}
                </ContextMenu.Item>
              ))}
            </ContextMenu.SubContent>
          </ContextMenu.Portal>
        </ContextMenu.Sub>
        <ContextMenu.Separator className="hn-menu-sep" />
        {inTrash ? (
          <>
            <ContextMenu.Item className="hn-menu-item" onSelect={() => restoreTrashedCard(card.id)}><Undo2 /> Restore</ContextMenu.Item>
            <ContextMenu.Item className="hn-menu-item danger" onSelect={() => void deleteForever(card)}><Trash2 /> Delete forever</ContextMenu.Item>
          </>
        ) : (
          <>
            <ContextMenu.Item className="hn-menu-item" onSelect={() => card.archivedAt ? restoreArchivedCard(card.id) : archiveCard(card.id)}>
              {card.archivedAt ? <ArchiveRestore /> : <Archive />} {card.archivedAt ? "Restore from archive" : "Archive"}
            </ContextMenu.Item>
            <ContextMenu.Item className="hn-menu-item danger" onSelect={() => trashCard(card.id)}><Trash2 /> Move to trash</ContextMenu.Item>
          </>
        )}
      </ContextMenu.Content>
    </ContextMenu.Portal>
  );
}

function CardActionsMenu({ card, boards, onOpen }: { card: WorkspaceItem; boards: WorkspaceItem[]; onOpen: () => void }) {
  const data = getData();
  const inTrash = !!card.trashedAt;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <span className={s.cardMenuTrigger} onDragStart={(event) => event.stopPropagation()}>
          <IconButton label="Card actions"><MoreHorizontal size={14} /></IconButton>
        </span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="hn-menu" align="end" sideOffset={5}>
          <DropdownMenu.Item className="hn-menu-item" onSelect={onOpen}>
            <Pencil /> Edit card
          </DropdownMenu.Item>
          {!inTrash ? (
            <DropdownMenu.Item className="hn-menu-item" onSelect={() => data.workspace.setPinned(card.id, !card.pinned)}>
              {card.pinned ? <PinOff /> : <Pin />} {card.pinned ? "Unpin" : "Pin"}
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger className="hn-menu-item">
              <LayoutGrid /> Move to board <span className="hn-menu-hint"><ChevronRight /></span>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className="hn-menu" sideOffset={4} alignOffset={-4}>
                <DropdownMenu.Item className="hn-menu-item" onSelect={() => moveCardToBoard(card.id, null)}>
                  {card.parentId === null ? <Check /> : <LayoutGrid />} All cards
                </DropdownMenu.Item>
                {boards.map((board) => (
                  <DropdownMenu.Item key={board.id} className="hn-menu-item" onSelect={() => moveCardToBoard(card.id, board.id)}>
                    {card.parentId === board.id ? <Check /> : <StickyNote />} {board.title || "Untitled"}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Separator className="hn-menu-sep" />
          {inTrash ? (
            <>
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => restoreTrashedCard(card.id)}>
                <Undo2 /> Restore
              </DropdownMenu.Item>
              <DropdownMenu.Item className="hn-menu-item danger" onSelect={() => void deleteForever(card)}>
                <Trash2 /> Delete forever
              </DropdownMenu.Item>
            </>
          ) : (
            <>
              <DropdownMenu.Item className="hn-menu-item" onSelect={() => card.archivedAt ? restoreArchivedCard(card.id) : archiveCard(card.id)}>
                {card.archivedAt ? <ArchiveRestore /> : <Archive />} {card.archivedAt ? "Restore from archive" : "Archive"}
              </DropdownMenu.Item>
              <DropdownMenu.Item className="hn-menu-item danger" onSelect={() => trashCard(card.id)}>
                <Trash2 /> Move to trash
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function CardEditor({ card, body, onBody, onClose }: { card: WorkspaceItem | null; body: string; onBody: (id: string, body: string) => void; onClose: () => void }) {
  const data = getData();
  if (!card) return null;
  const images = cardImages(body);
  const links = cardLinks(body);
  const inTrash = !!card.trashedAt;
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.editor} style={{ background: `var(--card-${card.colorKey ?? "gray"})` }}>
          <Dialog.Title className={s.srOnly}>Edit card</Dialog.Title>
          {images.length ? <div className={[s.editorImages, images.length > 1 ? s.imageGridMany : ""].join(" ")}>{images.map((img, i) => <img key={`${img.url}-${i}`} src={img.url} alt={img.alt} />)}</div> : null}
          <div className={s.editorFields}>
            <input className={s.editorTitle} value={card.title} readOnly={inTrash} onChange={(e) => data.workspace.rename(card.id, e.target.value)} placeholder="Title" />
            <textarea className={s.editorBody} value={cardText(body)} readOnly={inTrash} onChange={(e) => {
              const media = images.map((img) => `![${img.alt}](${img.url})`).join("\n");
              onBody(card.id, `${e.target.value}${media ? `\n${media}` : ""}`);
            }} placeholder="Take a note..." rows={8} />
            {links.map((url) => <LinkPreview key={url} url={url} />)}
          </div>
          <div className={s.editorActions}>
            {inTrash ? (
              <>
                <IconButton label="Restore" onClick={() => { restoreTrashedCard(card.id); onClose(); }}><Undo2 size={16} /></IconButton>
                <IconButton label="Delete forever" onClick={() => { onClose(); void deleteForever(card); }}><Trash2 size={16} /></IconButton>
              </>
            ) : (
              <>
                <PhotoPicker onFiles={(files) => void uploadPhotos(card.id, body, files, onBody)} />
                <ColorPicker value={card.colorKey ?? "gray"} onChange={(value) => data.workspace.setColorKey(card.id, value === "gray" ? undefined : value)} />
                <IconButton label={card.pinned ? "Unpin" : "Pin"} active={!!card.pinned} onClick={() => data.workspace.setPinned(card.id, !card.pinned)}>{card.pinned ? <PinOff size={16} /> : <Pin size={16} />}</IconButton>
                <IconButton label={card.archivedAt ? "Restore from archive" : "Archive"} onClick={() => { card.archivedAt ? restoreArchivedCard(card.id) : archiveCard(card.id); onClose(); }}>{card.archivedAt ? <ArchiveRestore size={16} /> : <Archive size={16} />}</IconButton>
                <IconButton label="Move to trash" onClick={() => { trashCard(card.id); onClose(); }}><Trash2 size={16} /></IconButton>
              </>
            )}
            <span className={s.spacer} />
            <button type="button" className={s.textButton} onClick={onClose}>Close</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

async function uploadPhotos(cardId: string, current: string, files: File[], onBody: (id: string, body: string) => void) {
  if (useAuth.getState().status !== "signedIn") {
    toast.info("Sign in to add photos. Photos count toward your storage quota.", { label: "Sign in", onClick: () => useSignInDialog.getState().setOpen(true) });
    return;
  }
  let next = current;
  for (const file of files) {
    try {
      const uploaded = await uploadAttachment(file, { noteId: cardId });
      next += `${next && !next.endsWith("\n") ? "\n" : ""}![${uploaded.name}](${uploaded.url})`;
      onBody(cardId, next);
    } catch (err) {
      toast.error(`Photo upload failed: ${err instanceof Error ? err.message : "unknown error"}`);
    }
  }
}

function PhotoPicker({ onFiles }: { onFiles: (files: File[]) => void }) {
  return <label className={s.iconLabel} title="Add image"><ImagePlus size={16} /><input type="file" accept="image/*" multiple onChange={(e) => { onFiles([...(e.target.files ?? [])]); e.target.value = ""; }} /></label>;
}

function ColorPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  return <div className={s.colorWrap}><IconButton label="Background color" active={open} onClick={() => setOpen(!open)}><Palette size={15} /></IconButton>{open ? <div className={s.colors}>{SHARED_FOLDER_COLORS.map((key) => <button key={key} type="button" aria-label={`${key} background`} className={s.color} style={{ background: `var(--card-${key})` }} onClick={() => { onChange(key); setOpen(false); }}>{value === key ? <Check size={12} /> : null}</button>)}</div> : null}</div>;
}

function LinkPreview({ url, compact = false }: { url: string; compact?: boolean }) {
  let parsed: URL;
  try { parsed = new URL(url); } catch { return null; }
  const video = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/.exec(url)?.[1];
  return <a className={[s.linkPreview, compact ? s.linkCompact : ""].join(" ")} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
    {video ? <img src={`https://i.ytimg.com/vi/${video}/hqdefault.jpg`} alt="Video preview" loading="lazy" /> : <span className={s.linkIcon}><Link2 size={16} /></span>}
    <span><strong>{parsed.hostname.replace(/^www\./, "")}</strong><small>{parsed.pathname === "/" ? url : parsed.pathname}</small></span>
  </a>;
}

async function deleteForever(card: WorkspaceItem): Promise<void> {
  if (!confirm(`Delete "${card.title || "Untitled card"}" forever? This cannot be undone.`)) return;
  const data = getData();
  const { cloudPending } = await deleteWorkspaceItemForever(data, card.id);
  toast.info(cloudPending ? "Card deleted here. Cloud cleanup will finish when you reconnect." : "Card deleted permanently");
}

function EmptyState({ mode, filtered }: { mode: CardMode; filtered: boolean }) {
  return <div className={s.empty}>{filtered ? <><Search size={34} /><span>No matching cards</span></> : mode === "archive" ? <><Archive size={34} /><span>Archived cards stay here forever</span></> : mode === "trash" ? <><Trash2 size={34} /><span>Trash is empty</span></> : <><StickyNote size={34} /><span>Cards you add appear here</span></>}</div>;
}
